import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
    ArrowLeft,
    Download,
    ExternalLink,
    FileText,
    Image as ImageIcon,
    Info,
    LayoutGrid,
    List,
    Paperclip,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import type { RecordData } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { confirm as confirmDialog } from "./ConfirmDialog";
import { uidLabel } from "./quickMetaHelpers";

// Record documents drawer (communication Enh 03 Slice 2) — the Documents
// chip's click surface. A right slide-over listing the record's
// `record_attachment` rows in place (no navigation; "back" = close), so it
// works for EVERY Attachable model. The full Documents app only appears as
// an "Open in Documents app →" escape hatch when the model is actually in
// the app's category tree (`_attachment_document_visible=True`).
//
// Capabilities mirror the Documents app's record view: upload (button +
// drag-drop), IN-DRAWER preview (click a file — no new tab), download,
// soft-delete (confirm), per-row (i) metadata popover, list|thumbnail view
// switcher, and a drag handle on the left edge (resizable MIN..MAX).
//
// Pure view (RecordDocumentsDrawerView) + thin container split, matching
// RecordStrip. Preview/download go through the authenticated blob routes —
// a plain <a href> would drop the bearer token.

// 1.5× the original 360/420 per user direction — files need the room.
export const DRAWER_MIN_WIDTH = 540;
export const DRAWER_MAX_WIDTH = 840;
const DRAWER_DEFAULT_WIDTH = 630;

export function clampDrawerWidth(px: number): number {
    const viewportCap = typeof window !== "undefined" ? Math.floor(window.innerWidth * 0.92) : DRAWER_MAX_WIDTH;
    return Math.min(Math.max(px, DRAWER_MIN_WIDTH), Math.min(DRAWER_MAX_WIDTH, viewportCap));
}

export interface DrawerDoc {
    uuid: string;
    name: string;
    sizeText: string;
    uploadedText: string;
    mimetype: string;
    ownerLabel: string;
    uploadedFullText: string;
}

export interface DrawerPreview {
    doc: DrawerDoc;
    /** Object URL over the authenticated blob. */
    url: string;
}

/** Map an ir.attachment wire row into the drawer's display shape. */
export function toDrawerDoc(row: RecordData): DrawerDoc {
    const uuid = typeof row.record_uuid === "string" ? row.record_uuid : String(row.id ?? "");
    const name = typeof row.name === "string" && row.name ? row.name : "(unnamed file)";
    const size = typeof row.size_bytes === "number" ? row.size_bytes : null;
    const uploadedRaw = typeof row.uploaded_at_utc === "string" ? row.uploaded_at_utc : "";
    return {
        uuid,
        name,
        sizeText: fmtSize(size),
        uploadedText: uploadedRaw.slice(0, 10),
        mimetype: typeof row.mimetype === "string" ? row.mimetype : "",
        ownerLabel: uidLabel(row.owner_id),
        uploadedFullText: uploadedRaw.replace("T", " ").slice(0, 16),
    };
}

export function fmtSize(bytes: number | null): string {
    if (bytes == null || Number.isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImage(mimetype: string): boolean {
    return mimetype.startsWith("image/");
}

export function isInlineViewable(mimetype: string): boolean {
    return isImage(mimetype) || mimetype === "application/pdf" || mimetype.startsWith("text/");
}

/** Per-doc action cluster — (i) metadata popover + download + delete. Shared
    by the list rows and the thumbnail cards so both modes act identically. */
function DocActions({
    doc,
    onDownload,
    onDelete,
}: {
    doc: DrawerDoc;
    onDownload: (doc: DrawerDoc) => void;
    onDelete: (doc: DrawerDoc) => void;
}): ReactNode {
    return (
        <>
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <button
                        type="button"
                        className="record-docs__icon-btn"
                        aria-label={`Details of ${doc.name}`}
                        title="Details"
                    >
                        <Info size={14} />
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content className="rdx-menu record-docs__meta" sideOffset={4} align="end">
                        <div className="record-docs__meta-grid">
                            <span className="record-docs__meta-k">Name</span>
                            <span className="record-docs__meta-v">{doc.name}</span>
                            <span className="record-docs__meta-k">Type</span>
                            <span className="record-docs__meta-v">{doc.mimetype || "—"}</span>
                            <span className="record-docs__meta-k">Size</span>
                            <span className="record-docs__meta-v">{doc.sizeText || "—"}</span>
                            <span className="record-docs__meta-k">Uploaded</span>
                            <span className="record-docs__meta-v">{doc.uploadedFullText || "—"}</span>
                            <span className="record-docs__meta-k">By</span>
                            <span className="record-docs__meta-v">{doc.ownerLabel}</span>
                        </div>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <button
                type="button"
                className="record-docs__icon-btn"
                onClick={() => onDownload(doc)}
                aria-label={`Download ${doc.name}`}
                title="Download"
            >
                <Download size={14} />
            </button>
            <button
                type="button"
                className="record-docs__icon-btn record-docs__icon-btn--danger"
                onClick={() => onDelete(doc)}
                aria-label={`Delete ${doc.name}`}
                title="Delete"
            >
                <Trash2 size={14} />
            </button>
        </>
    );
}

interface RecordDocumentsDrawerViewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    docs: DrawerDoc[];
    /** uuid → object URL for image-file thumbnails (grid mode). */
    thumbs: Record<string, string>;
    loading: boolean;
    uploading: boolean;
    /** Render the "Open in Documents app →" escape hatch. */
    appLinkVisible: boolean;
    /** In-drawer preview — when set, the body shows the file, not the list. */
    preview: DrawerPreview | null;
    onClosePreview: () => void;
    onOpenInApp: () => void;
    onPreview: (doc: DrawerDoc) => void;
    onDownload: (doc: DrawerDoc) => void;
    onDelete: (doc: DrawerDoc) => void;
    onUpload: (files: File[]) => void;
}

export function RecordDocumentsDrawerView({
    open,
    onOpenChange,
    title,
    docs,
    thumbs,
    loading,
    uploading,
    appLinkVisible,
    preview,
    onClosePreview,
    onOpenInApp,
    onPreview,
    onDownload,
    onDelete,
    onUpload,
}: RecordDocumentsDrawerViewProps): ReactNode {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [width, setWidth] = useState(DRAWER_DEFAULT_WIDTH);
    const [dragOver, setDragOver] = useState(false);
    const [mode, setMode] = useState<"list" | "grid">("grid");

    const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        const onMove = (ev: PointerEvent) => setWidth(clampDrawerWidth(window.innerWidth - ev.clientX));
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    const pickFiles = (list: FileList | null) => {
        const files = Array.from(list ?? []);
        if (files.length) onUpload(files);
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" />
                <Dialog.Content
                    className={`record-docs${dragOver ? " record-docs--dragover" : ""}`}
                    aria-describedby={undefined}
                    style={{ width }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        pickFiles(e.dataTransfer?.files ?? null);
                    }}
                >
                    {/* drag handle — resize between MIN/MAX */}
                    <div
                        className="record-docs__resize"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize documents drawer"
                        onPointerDown={startResize}
                    />

                    <header className="record-docs__head">
                        {preview ? (
                            <>
                                <button
                                    type="button"
                                    className="record-docs__icon-btn"
                                    onClick={onClosePreview}
                                    aria-label="Back to documents"
                                    title="Back"
                                >
                                    <ArrowLeft size={15} />
                                </button>
                                <Dialog.Title className="record-docs__title">{preview.doc.name}</Dialog.Title>
                                <button
                                    type="button"
                                    className="record-docs__icon-btn"
                                    onClick={() => onDownload(preview.doc)}
                                    aria-label={`Download ${preview.doc.name}`}
                                    title="Download"
                                >
                                    <Download size={14} />
                                </button>
                            </>
                        ) : (
                            <>
                                <Paperclip size={15} aria-hidden="true" />
                                <Dialog.Title className="record-docs__title">
                                    Documents — {title}
                                    <span className="record-docs__count">{docs.length}</span>
                                </Dialog.Title>
                                <div className="record-docs__mode" role="group" aria-label="View mode">
                                    <button
                                        type="button"
                                        className={`record-docs__mode-btn${mode === "list" ? " is-active" : ""}`}
                                        onClick={() => setMode("list")}
                                        aria-label="List view"
                                        title="List"
                                    >
                                        <List size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`record-docs__mode-btn${mode === "grid" ? " is-active" : ""}`}
                                        onClick={() => setMode("grid")}
                                        aria-label="Thumbnail view"
                                        title="Thumbnails"
                                    >
                                        <LayoutGrid size={13} />
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    className="record-docs__upload"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                >
                                    <Upload size={13} aria-hidden="true" />
                                    {uploading ? "Uploading…" : "Upload"}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="record-docs__file-input"
                                    aria-label="Upload files"
                                    onChange={(e) => {
                                        pickFiles(e.target.files);
                                        e.target.value = "";
                                    }}
                                />
                            </>
                        )}
                        <Dialog.Close className="record-docs__close" aria-label="Close">
                            <X size={15} />
                        </Dialog.Close>
                    </header>

                    {preview ? (
                        <div className="record-docs__preview">
                            {isImage(preview.doc.mimetype) ? (
                                <img className="record-docs__preview-img" src={preview.url} alt={preview.doc.name} />
                            ) : isInlineViewable(preview.doc.mimetype) ? (
                                <iframe className="record-docs__preview-frame" src={preview.url} title={preview.doc.name} />
                            ) : (
                                <div className="record-docs__preview-fallback">
                                    <FileText size={28} aria-hidden="true" />
                                    <p>No inline preview for this file type.</p>
                                    <button
                                        type="button"
                                        className="record-docs__upload"
                                        onClick={() => onDownload(preview.doc)}
                                    >
                                        <Download size={13} aria-hidden="true" />
                                        Download
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="record-docs__body">
                            {loading ? (
                                <p className="record-docs__empty">Loading…</p>
                            ) : docs.length === 0 ? (
                                <p className="record-docs__empty">
                                    No documents attached to this record yet.
                                    <br />
                                    Drop files here or use Upload.
                                </p>
                            ) : mode === "list" ? (
                                docs.map((doc) => (
                                    <div className="record-docs__row" key={doc.uuid}>
                                        <button
                                            type="button"
                                            className="record-docs__file"
                                            onClick={() => onPreview(doc)}
                                            title="Preview"
                                        >
                                            <FileText size={15} aria-hidden="true" />
                                            <span className="record-docs__file-meta">
                                                <span className="record-docs__file-name">{doc.name}</span>
                                                <span className="record-docs__file-sub">
                                                    {[doc.sizeText, doc.uploadedText].filter(Boolean).join(" · ")}
                                                </span>
                                            </span>
                                        </button>
                                        <DocActions doc={doc} onDownload={onDownload} onDelete={onDelete} />
                                    </div>
                                ))
                            ) : (
                                <div className="record-docs__grid">
                                    {docs.map((doc) => (
                                        <div className="record-docs__card" key={doc.uuid}>
                                            <button
                                                type="button"
                                                className="record-docs__card-body"
                                                onClick={() => onPreview(doc)}
                                                title="Preview"
                                            >
                                                <span className="record-docs__card-visual">
                                                    {thumbs[doc.uuid] ? (
                                                        <img
                                                            className="record-docs__card-thumb"
                                                            src={thumbs[doc.uuid]}
                                                            alt={doc.name}
                                                        />
                                                    ) : isImage(doc.mimetype) ? (
                                                        <ImageIcon size={26} aria-hidden="true" />
                                                    ) : (
                                                        <FileText size={26} aria-hidden="true" />
                                                    )}
                                                </span>
                                                <span className="record-docs__card-name">{doc.name}</span>
                                                <span className="record-docs__card-sub">{doc.sizeText}</span>
                                            </button>
                                            <div className="record-docs__card-actions">
                                                <DocActions doc={doc} onDownload={onDownload} onDelete={onDelete} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!preview && appLinkVisible ? (
                        <footer className="record-docs__foot">
                            <button type="button" className="record-docs__app-link" onClick={onOpenInApp}>
                                <ExternalLink size={13} aria-hidden="true" />
                                Open in Documents app
                            </button>
                        </footer>
                    ) : null}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

interface RecordDocumentsDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    modelKey: string;
    recordUuid: string;
    title: string;
    /** Navigates to the Documents app deep link (escape hatch). */
    onOpenInApp: () => void;
    /** Fired after a successful upload/delete so the strip can refresh counts. */
    onChanged?: () => void;
}

export function RecordDocumentsDrawer({
    open,
    onOpenChange,
    modelKey,
    recordUuid,
    title,
    onOpenInApp,
    onChanged,
}: RecordDocumentsDrawerProps): ReactNode {
    const runtime = useRuntime();
    const [docs, setDocs] = useState<DrawerDoc[]>([]);
    const [thumbs, setThumbs] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [appLinkVisible, setAppLinkVisible] = useState(false);
    const [preview, setPreview] = useState<DrawerPreview | null>(null);
    const thumbsRef = useRef<Record<string, string>>({});

    const closePreview = () => {
        setPreview((p) => {
            if (p) URL.revokeObjectURL(p.url);
            return null;
        });
    };

    const clearThumbs = () => {
        for (const url of Object.values(thumbsRef.current)) URL.revokeObjectURL(url);
        thumbsRef.current = {};
        setThumbs({});
    };

    const reload = async () => {
        try {
            const rows = await runtime.services.chatter.listAttachments(modelKey, recordUuid);
            const mapped = rows.map(toDrawerDoc);

            // The wire ships owner_id as a raw res.user uuid (no server-side
            // M2O resolution on this endpoint) — batch-resolve display names
            // with one generic search so the (i) popover shows "Priya Nair",
            // not "64e120a1…". Non-fatal: uuids stay truncated on failure.
            const rawOwners = rows.map((r) => (typeof r.owner_id === "string" ? r.owner_id : null));
            const unresolved = [...new Set(rawOwners.filter((v): v is string => Boolean(v)))];
            if (unresolved.length) {
                try {
                    const res = await runtime.services.record.search("res.user", {
                        domain: [["record_uuid", "in", unresolved]],
                        limit: unresolved.length,
                    });
                    const names = new Map(
                        res.records.map((u) => [
                            String(u.record_uuid ?? u.id ?? ""),
                            typeof u.name === "string" && u.name ? u.name : uidLabel(u),
                        ]),
                    );
                    mapped.forEach((doc, i) => {
                        const raw = rawOwners[i];
                        const resolvedName = raw ? names.get(raw) : undefined;
                        if (resolvedName) doc.ownerLabel = resolvedName;
                    });
                } catch {
                    /* keep uuid-prefix labels */
                }
            }
            setDocs(mapped);

            // Lazy image thumbnails for grid mode — authenticated preview blob
            // per image file (capped; object URLs tracked for revocation).
            const imageDocs = mapped.filter((d) => isImage(d.mimetype)).slice(0, 24);
            for (const doc of imageDocs) {
                if (thumbsRef.current[doc.uuid]) continue;
                runtime.services.chatter
                    .previewAttachment(doc.uuid)
                    .then((blob) => {
                        const url = URL.createObjectURL(blob);
                        thumbsRef.current = { ...thumbsRef.current, [doc.uuid]: url };
                        setThumbs(thumbsRef.current);
                    })
                    .catch(() => {
                        /* icon fallback */
                    });
            }
        } catch {
            setDocs([]);
        }
    };

    useEffect(() => {
        if (!open) {
            closePreview();
            clearThumbs();
            return;
        }
        let active = true;
        setLoading(true);
        void reload().finally(() => {
            if (active) setLoading(false);
        });
        runtime.services.chatter
            .isDocumentsAppVisible(modelKey)
            .then((visible) => {
                if (active) setAppLinkVisible(visible);
            })
            .catch(() => {
                if (active) setAppLinkVisible(false);
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, modelKey, recordUuid]);

    const onPreviewDoc = async (doc: DrawerDoc) => {
        try {
            const blob = await runtime.services.chatter.previewAttachment(doc.uuid);
            const url = URL.createObjectURL(blob);
            setPreview((prev) => {
                if (prev) URL.revokeObjectURL(prev.url);
                return { doc, url };
            });
        } catch {
            /* preview failure is non-fatal — list stays */
        }
    };

    const onDownloadDoc = async (doc: DrawerDoc) => {
        try {
            const blob = await runtime.services.chatter.downloadAttachment(doc.uuid);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.name;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 30_000);
        } catch {
            /* download failure is non-fatal */
        }
    };

    const onUpload = async (files: File[]) => {
        setUploading(true);
        try {
            for (const file of files) {
                await runtime.services.chatter.uploadAttachment(modelKey, recordUuid, file);
            }
            await reload();
            onChanged?.();
        } catch {
            await reload(); // partial multi-file upload — show what landed
        } finally {
            setUploading(false);
        }
    };

    const onDelete = async (doc: DrawerDoc) => {
        const ok = await confirmDialog({
            title: "Delete document?",
            message: `"${doc.name}" will be archived (recoverable from Trash).`,
            confirmLabel: "Delete",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await runtime.services.chatter.deleteAttachment(doc.uuid);
            await reload();
            onChanged?.();
        } catch {
            /* row stays — delete failed */
        }
    };

    return (
        <RecordDocumentsDrawerView
            open={open}
            onOpenChange={onOpenChange}
            title={title}
            docs={docs}
            thumbs={thumbs}
            loading={loading}
            uploading={uploading}
            appLinkVisible={appLinkVisible}
            preview={preview}
            onClosePreview={closePreview}
            onOpenInApp={onOpenInApp}
            onPreview={(doc) => void onPreviewDoc(doc)}
            onDownload={(doc) => void onDownloadDoc(doc)}
            onDelete={(doc) => void onDelete(doc)}
            onUpload={(files) => void onUpload(files)}
        />
    );
}
